import { forwardRef, ReactNode, useCallback, useEffect, useState } from 'react';

import cx from 'clsx';
import { AiOutlineClockCircle } from 'react-icons/ai';
import { RiCloseLine } from 'react-icons/ri';

import {
  ActionIcon,
  Box,
  BoxProps,
  Grid,
  MantineSize,
  SegmentedControl,
  Stack,
  FloatingPosition,
  rem
} from '@mantine/core';
import { dayjs } from '@ui';

import { TextInput, TextInputProps } from '../../inputs';
import { Popover } from '../../overlays';
import { Text } from '../../typography';

import classes from './TimePicker.module.css';

type ModifiedTextInputProps = Omit<TextInputProps, 'onChange' | 'value'>;

export interface TimePickerProps extends BoxProps, ModifiedTextInputProps {
  /**
   * Size of the input
   */
  size?: MantineSize;
  /**
   * Label for the text input
   */
  label?: string;
  /**
   * placeholder for the text input
   */
  placeholder?: string;
  /**
   * description shown in the widget
   */
  description?: string;
  /**
   * icon for the text input
   */
  leftSection?: ReactNode;
  /**
   * Right side for text input
   */
  rightSection?: ReactNode;
  /**
   * disable the widget
   */
  disabled?: boolean;
  /**
   * loading
   */
  loading?: boolean;
  /**
   * error if any
   */
  error?: string;
  /**
   * values
   * */
  value?: Date;
  /**
   *
   */
  minuteType?: '30' | '15';
  /**
   * enable clear
   */
  clearable?: boolean;
  /**
   * positioning the dropdown
   */
  position?: FloatingPosition;
  /**
   * triggered when value changes
   */
  onChange?: (value: string) => void;
  /**
   * min time
   */
  minTime?: Date;
  /**
   * max time
   */
  maxTime?: Date;
  /**
   * To enable the all the minutes if the date is not selected when linked to a DatePicker
   */
  enableAllMinutes?: boolean;
  /**
   *disable minute segmented control.
   */
  disableMinuteSelection?: boolean;
  /**
   * runValueChecks
   */
  runValueChecks?: boolean;
}

export function padTime(value: string) {
  const _val = parseInt(value, 10);

  return _val >= 10 ? _val.toString() : `0${_val}`;
}

export function getTimeValues(value: Date | undefined, format: '12' | '24') {
  if (!(value instanceof Date)) {
    return { hours: '', minutes: '', seconds: '', amPm: '' };
  }

  let _hours = value.getHours();

  const isPm = _hours >= 12;
  if (format === '12') {
    _hours %= 12;
    if (_hours === 0) {
      _hours += 12;
    }
  }

  return {
    hours: _hours.toString(),
    minutes: value.getMinutes().toString(),
    seconds: value.getSeconds().toString(),
    amPm: isPm ? 'PM' : 'AM'
  };
}

export const getCurrentMeridiem = () => {
  const currDate = new Date();
  if (currDate > new Date(dayjs().hour(11).minute(30).second(0).toString())) {
    return 'PM';
  }
  return 'AM';
};

const TIME_HOURS_LIST = [10, 11, 12, 1, 2, 3, 4, 5, 6, 7, 8, 9];

const TimePicker = forwardRef<HTMLInputElement, TimePickerProps>(
  (
    {
      size,
      label,
      description,
      placeholder,
      error,
      leftSection = <AiOutlineClockCircle />,
      disabled,
      rightSection,
      loading,
      value,
      minuteType = '30',
      clearable,
      position = 'bottom-end',
      onChange,
      minTime,
      maxTime,
      enableAllMinutes,
      disableMinuteSelection,
      runValueChecks,
      classNames,
      ...rest
    },
    ref
  ) => {
    const timeValues = getTimeValues(value, '12');

    const [hour, setHour] = useState<number | undefined>(
      timeValues.hours ? parseInt(timeValues.hours) : undefined
    );
    const [fraction, setFraction] = useState<string>(
      timeValues.minutes ? timeValues.minutes : '0'
    );
    const [meridiem, setMeridiem] = useState<string>(
      timeValues.amPm ? timeValues.amPm : getCurrentMeridiem()
    );
    const [filteredRange, setFilteredRange] = useState<number[]>([]);

    const clearState = () => {
      setHour(undefined);
      setFraction('0');
      setMeridiem(getCurrentMeridiem());
    };

    useEffect(() => {
      // only running for controlled instances. This picker is complicated.
      if (runValueChecks) {
        setHour(timeValues.hours ? parseInt(timeValues.hours) : undefined);
        setFraction(timeValues.minutes ? timeValues.minutes : '0');
        setMeridiem(timeValues.amPm ? timeValues.amPm : getCurrentMeridiem());
      }
    }, [value]);

    //If the timepicker is linked to a DatePicker then we need to enable all the minutes in the Timepicker if Date is not selected.
    //For this we use the variable enableAllMinutes.
    //If the Date is selected then we enable the minutes as per the logic below.
    const getMinuteStatus = (minute: string) => {
      if (hour) {
        if (maxTime || minTime) {
          if (enableAllMinutes) {
            return false;
          } else {
            const currentHour = dayjs().format('h');
            const currentMinute = dayjs().minute().toString();
            if (
              !minTime &&
              hour.toString() === currentHour &&
              minute < currentMinute
            ) {
              return true;
            }

            // Incase both hours are same i.e 10am and 10pm, then same minutes will be disabled for both.
            // Therefore we check if the current meridiem matches with the meridiem of the minTime and maxTime.
            if (
              hour.toString() === dayjs(minTime).format('h') &&
              hour.toString() === dayjs(maxTime).format('h')
            ) {
              // check for the minTime, incase 10:30 is given for minTime then we need to disable 10:15 and 10:00
              if (
                minTime &&
                getTimeValues(minTime, '12').amPm === meridiem &&
                hour.toString() === dayjs(minTime).format('h') &&
                minute < dayjs(minTime).minute().toString()
              ) {
                return true;
              }
              // check for the maxTime, incase 10:00 is given for maxTime then we need to disable 10:15 and 10:30
              if (
                maxTime &&
                getTimeValues(maxTime, '12').amPm === meridiem &&
                hour.toString() === dayjs(maxTime).format('h') &&
                minute > dayjs(maxTime).minute().toString()
              ) {
                return true;
              }
            } else {
              // check for the minTime, incase 10:30 is given for minTime then we need to disable 10:15 and 10:00
              if (
                minTime &&
                hour.toString() === dayjs(minTime).format('h') &&
                minute < dayjs(minTime).minute().toString()
              ) {
                return true;
              }
              // check for the maxTime, incase 10:00 is given for maxTime then we need to disable 10:15 and 10:30
              if (
                maxTime &&
                hour.toString() === dayjs(maxTime).format('h') &&
                minute > dayjs(maxTime).minute().toString()
              ) {
                return true;
              }
            }
          }
        }
      }
      return false;
    };

    const MINUTE_OPTIONS_30 = [
      { label: '00', value: '0', disabled: getMinuteStatus('0') },
      { label: '30', value: '30', disabled: getMinuteStatus('30') }
    ];

    const MINUTE_OPTIONS_15 = [
      { label: '00', value: '0', disabled: getMinuteStatus('0') },
      { label: '15', value: '15', disabled: getMinuteStatus('15') },
      { label: '30', value: '30', disabled: getMinuteStatus('30') },
      { label: '45', value: '45', disabled: getMinuteStatus('45') }
    ];

    //To prevent the disabled option from being selected when we change the hour
    useEffect(() => {
      if (minTime || maxTime) {
        if (minuteType === '30') {
          setFraction(
            MINUTE_OPTIONS_30.find((item) => item.disabled === false)?.value || '0'
          );
          handleMinuteChange(
            MINUTE_OPTIONS_30.find((item) => item.disabled === false)?.value || '0'
          );
        } else {
          setFraction(
            MINUTE_OPTIONS_15.find((item) => item.disabled === false)?.value || '0'
          );
          handleMinuteChange(
            MINUTE_OPTIONS_15.find((item) => item.disabled === false)?.value || '0'
          );
        }
      }
    }, [hour]);

    useEffect(() => {
      //Everytime the meridiem changes we are resetting hour and minutes inorder to prevent the selection of disabled option.
      if ((minTime || maxTime) && !filteredRange.find((item) => item === hour)) {
        setHour(undefined);
        setFraction('0');
        onChange?.('');
      }
    }, [meridiem, filteredRange]);

    useEffect(() => {
      const filteredList = TIME_HOURS_LIST.filter((hour) => {
        // create a Date object for the current hour with the given meridiem

        const hourDateMin = dayjs(
          `${dayjs().format('YYYY-MM-DD')} ${hour}:${dayjs(minTime)
            .minute()
            .toString()}:00 ${meridiem}`
        );

        const hourDateMax = dayjs(
          `${dayjs().format('YYYY-MM-DD')} ${hour}:${dayjs(maxTime)
            .minute()
            .toString()}:00 ${meridiem}`
        );

        const minTimeDayjs = minTime
          ? dayjs(`${dayjs(minTime).format('YYYY-MM-DD HH:mm')}:00`)
          : null;
        const maxTimeDayjs = maxTime
          ? dayjs(`${dayjs(maxTime).format('YYYY-MM-DD HH:mm')}:00`)
          : null;

        // check if the hourDateMin is greater than or equal to minTime and hourDateMax is less than or equal to maxTime
        // If minTime or maxTime is not present we include all values in the filtered list
        return (
          (minTime ? hourDateMin.isSameOrAfter(dayjs(minTimeDayjs)) : true) &&
          (maxTime ? hourDateMax.isSameOrBefore(dayjs(maxTimeDayjs)) : true)
        );
      });
      setFilteredRange(filteredList);
    }, [meridiem, minTime]); //Adding minTime to the dependency array to get the time range if the date is selected as today's date.

    const getInputValue = useCallback(() => {
      return value ? dayjs(value).format('hh:mm a') : '';
    }, [value]);

    const handleChange = (latestVal: Date) => {
      onChange?.(latestVal.toString());
    };

    const clearValue = () => {
      clearState();
      onChange?.('');
    };

    const getHoursIn24HourFormat = (timeHr: number, currMer: string) => {
      if (currMer === 'AM') {
        if (timeHr === 12) {
          return 0;
        }
        return timeHr;
      } else {
        if (timeHr === 12) {
          return 12;
        }
        return timeHr + 12;
      }
    };

    const handleHourChange = (timeHr: number) => {
      setHour(timeHr);

      const timeAsDate = dayjs()
        .hour(getHoursIn24HourFormat(timeHr, meridiem))
        .minute(parseInt(fraction));

      handleChange(timeAsDate.toDate());
    };

    const handleMinuteChange = (minute: string) => {
      setFraction(minute);
      if (hour) {
        const timeAsDate = dayjs()
          .hour(getHoursIn24HourFormat(hour!, meridiem))
          .minute(parseInt(minute));

        handleChange(timeAsDate.toDate());
      }
    };

    const handleMeridiemChange = (mer: string) => {
      setMeridiem(mer);
      if (hour) {
        const timeAsDate = dayjs()
          .hour(getHoursIn24HourFormat(hour!, mer))
          .minute(parseInt(fraction));

        handleChange(timeAsDate.toDate());
      }
    };

    return (
      <Box pos="relative" {...rest}>
        <Popover
          disabled={disabled}
          styles={{
            dropdown: {
              padding: 8,
              marginTop: -4,
              width: rem(260),
              maxWidth: rem(260),
              left: undefined
            }
          }}
          position={position}
          removePadding
        >
          <Popover.Target>
            <TextInput
              ref={ref}
              leftSection={leftSection}
              value={getInputValue()}
              readOnly
              disabled={disabled}
              size={size}
              label={label}
              description={description}
              placeholder={placeholder}
              rightSection={
                rightSection ? (
                  rightSection
                ) : value && clearable ? (
                  <ActionIcon
                    onClick={clearValue}
                    size="xs"
                    variant="transparent"
                    color="secondary"
                    aria-label="Clear"
                  >
                    <RiCloseLine />
                  </ActionIcon>
                ) : undefined
              }
              enableRightSectionEvents={
                !loading && !rightSection && !error && !value
              }
              error={error}
              loading={loading}
              classNames={classNames}
              styles={{ input: { cursor: 'pointer' } }}
            />
          </Popover.Target>
          <Popover.Dropdown>
            <Grid>
              <Grid.Col span={8} className={classes.colHeader}>
                Hour
              </Grid.Col>
              <Grid.Col span={4} className={classes.colHeader}>
                Minutes
              </Grid.Col>
              <Grid.Col span={8}>
                <Grid gutter={0} className={classes.hrMatContainer}>
                  {TIME_HOURS_LIST.map((timeHr) => {
                    const selected = hour === timeHr;
                    return (
                      <Grid.Col
                        span={4}
                        key={timeHr}
                        className={classes.hrMatHour}
                        onClick={() => {
                          if (filteredRange.find((item) => item === timeHr)) {
                            handleHourChange(timeHr);
                          }
                        }}
                      >
                        <Box
                          className={cx(classes.hrMatHourContent, {
                            [classes.hrMatHourContentActive]: selected,
                            [classes.disabled]: !filteredRange.find(
                              (item) => item === timeHr
                            )
                          })}
                        >
                          {padTime(timeHr.toString())}
                        </Box>
                      </Grid.Col>
                    );
                  })}
                </Grid>
              </Grid.Col>
              <Grid.Col span={4}>
                <Stack gap="xs">
                  <SegmentedControl
                    disabled={!!disableMinuteSelection}
                    data={
                      minuteType === '30' ? MINUTE_OPTIONS_30 : MINUTE_OPTIONS_15
                    }
                    onChange={handleMinuteChange}
                    orientation="vertical"
                    size="xs"
                    radius="xs"
                    fullWidth
                    value={fraction}
                  />

                  <Text fz={10} lightColor="text.5" darkColor="text.3" ta="center">
                    am / pm
                  </Text>

                  <SegmentedControl
                    data={[
                      { label: 'AM', value: 'AM' },
                      { label: 'PM', value: 'PM' }
                    ]}
                    size="xs"
                    onChange={handleMeridiemChange}
                    radius="xs"
                    value={meridiem}
                  />
                </Stack>
              </Grid.Col>
            </Grid>
          </Popover.Dropdown>
        </Popover>
      </Box>
    );
  }
);

export { TimePicker };
